/**
 * ColumnAddPopover — Designer-side `[+ Column]` button + popover that mounts
 * `ColumnAddFormBody`. Replaces the `DisabledAffordance` placeholder shipped
 * by Slice 0.4. Re-used by the Designer's `ColumnsTab`. Inline (Variant 1)
 * exposes the same form via `InlineDdlColumnPickerPopover`'s add stage.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A2.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ColumnAddFormBody } from './ColumnAddFormBody';

export interface ColumnAddPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function ColumnAddPopover(props: ColumnAddPopoverProps) {
  const { editContext, testId = 'column-add-popover', onCommitted } = props;
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add column"
        >
          <Plus className="h-3.5 w-3.5" />
          Column
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="flex max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 flex-col overflow-hidden"
        data-testid={testId}
      >
        {open ? (
          <ColumnAddFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
